import { EAuthenticationType, ESdChangeReason, IDatasourceDto } from "@kuika/kuika-cl-model";
import {
  HTTPMethod,
  IRestApiEndpoint,
  IRestApiEndpointOperationRequest,
  IRestApiEndpointOperationResponse,
  IRestApiSchema,
  IRestServiceConnectionDto
} from "@kuika/kuika-cl-model/src/dtos/datasource.dto";
import ObjectHash from "object-hash";
import { FormInstance } from "rc-field-form";
import { KeyboardEvent, ReactNode, createRef } from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IPureDesignComponentProps, PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import { UnsavedChangesModalModuleActionTypes } from "../../../store/unsaved-changes-modal/types";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { InputTooltip } from "../../common/input-tooltip";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEntitySwitcher, { KuikaEntitySwitcherItem } from "../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import KuikaFormPage, {
  KuikaFormAsideBlock,
  KuikaFormField,
  KuikaFormSection
} from "../../ui-kit/kuika-form-page/kuika-form-page";
import KuikaTabs, { KuikaTabOption } from "../../ui-kit/kuika-tabs/kuika-tabs";
import { ensureTrailingDraftRow, withoutBlankRows } from "../shared/kuika-edit-grid/draft-rows";
import { ConnectionRenameTitle } from "./connection-rename-title";
import { requestDatasourceReveal } from "./datasource-reveal";
import "./create-connection-form.scss";
import "./editor-bar-surface.scss";
import { getEditorGlyph, IEditorGlyph } from "./editor-switcher/editor-glyph";
import { EditorGlyphIcon } from "./editor-switcher/editor-glyph-icon";
import { RestApiEndpointBodyTab } from "./rest-api/rest-api-endpoint-body-tab";
import { RestConnectionAuthSection } from "./rest-api/rest-connection-auth-section";
import {
  blankHeaderParameter,
  buildHeaderRecord,
  headerParametersForAuthType,
  IApiKeyParameter,
  IBasicAuthCredentials,
  IOrganizedHeaderParameters,
  IParsedHeaderParameters,
  isBlankHeaderParameter,
  organizeHeaderParameters,
  parseHeaderRecord
} from "./rest-api/rest-connection-headers";
import {
  blankEndpointParameter,
  ENDPOINT_PARAMETERS_PATH,
  IRestEndpointParameterRow,
  isBlankEndpointParameter,
  RestEndpointParametersTable,
  withoutBlankEndpointParameters
} from "./rest-api/rest-endpoint-parameters-table";
import { IRestEndpointRequest, RestEndpointRequestRow } from "./rest-api/rest-endpoint-request-row";
import { buildAuthTypeTabOptions, ENDPOINT_AUTH_TYPES, REST_WO_SWAGGER_SPEC } from "./rest-connection-specs";

interface IEndpointValidationErrorField {
  errors: string[];
}

interface IEndpointValidationError {
  errorFields?: IEndpointValidationErrorField[];
}

/** An endpoint lives in a REST collection, so its guide is the collection's. */
const REST_API_GUIDE: string = REST_WO_SWAGGER_SPEC.helpLink;

interface ICreateRestApiEndpointProps {
  editingDatasource: IDatasourceDto;
  editingEndpointIndex: number;
  showModal: boolean;
  nextCallback?: () => void;
  setNextCallbackFunction: (cb?: () => void) => void;
  setShowModal: (boolean) => void;
}

interface ICreateRestApiEndpointState {
  endpointName: string;
  /** The RUN of the endpoint, which shares the page's busy flag. The save has its own status. */
  isSaveDisabled: boolean;
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  isRequestBodyEditable: boolean;
  isNameOnEdit: boolean;
  authType: EAuthenticationType;
  request: {
    schema?: IRestApiEndpointOperationRequest["schema"];
    type?: IRestApiEndpointOperationRequest["type"];
  };
  responses: {
    schema?: IRestApiEndpointOperationResponse["schema"];
    type?: IRestApiEndpointOperationResponse["type"];
    errorMessage?: IRestApiEndpointOperationResponse["errorMessage"];
  }[];
  schemas: IRestApiSchema[];
  isResponseBodyEdited: boolean;
  userNameHeaderParameterIndex: number;
  authTokenHeaderParameterIndex: number;
  basicAuthHeaderParameterIndex: number;
  showPlaceholderOnResponseBody: boolean;
}

class CreateRestApiEndpoint extends PureSdComponent<
  ICreateRestApiEndpointProps & IWithSDProps,
  ICreateRestApiEndpointState
> {
  formRef = createRef<FormInstance>();

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  executeCheckerHash: string = "";

  constructor(props: any) {
    super(props);
    this.state = {
      endpointName: "MyEndpoint",
      isSaveDisabled: false,
      saveStatus: "idle",
      isRequestBodyEditable: false,
      isNameOnEdit: false,
      authType: EAuthenticationType.InheritFromCollection,
      request: { schema: null, type: "object" },
      responses: [{ schema: null, type: "object", errorMessage: "" }],
      schemas: [],
      isResponseBodyEdited: false,
      userNameHeaderParameterIndex: -1,
      authTokenHeaderParameterIndex: -1,
      basicAuthHeaderParameterIndex: -1,
      showPlaceholderOnResponseBody: true
    };
  }

  validateHttpRequest = async (_rule: any, value?: IRestEndpointRequest) => {
    if (!value) throw new Error("Please enter Endpoint's HTTP Request Info");
    if (!value.method) throw new Error("Please enter Endpoint's HTTP Request Method");
    if (!value.path) throw new Error("Please enter Endpoint's HTTP Request Path");
    if (value.path[0] === "/") throw new Error("Endpoint's HTTP Request Path cannot start with '/' character");

    const { editingDatasource, editingEndpointIndex } = this.props;
    // The grid's draft row is not a parameter, and a Path one would otherwise demand `{}` in the path.
    const parameters: IRestEndpointParameterRow[] = withoutBlankEndpointParameters(
      this.formRef.current?.getFieldValue(ENDPOINT_PARAMETERS_PATH)
    );

    if (parameters.length) {
      parameters.forEach((p: IRestEndpointParameterRow): void => {
        if (p.location === "Path") {
          if (value.path && !value.path.includes(`{${p.key}}`)) {
            throw new Error(
              `Endpoint's Path Parameter [${p.key}] is not entered on endpoint path. Please insert '{${p.key}}' on endpoint's path`
            );
          }
        }
      });
    }

    const isSameHTTPRequestExists = editingDatasource.restApiConnectionRequest.paths?.find(
      (endpoint, i) =>
        endpoint.path === `/${value.path}` && endpoint.operations[0].type === value.method && i !== editingEndpointIndex
    );
    if (isSameHTTPRequestExists)
      throw new Error(`This HTTP ${value.method} request with path "/${value.path}" is saved before`);
  };

  existingEndpointNameValidator = async (_rule, value: string) => {
    const { editingDatasource, editingEndpointIndex } = this.props;

    const paths = editingDatasource.restApiConnectionRequest.paths;

    if (!paths?.length) return;

    const isExists = paths.find(
      (endpoint, i) => endpoint.operations[0].operationName === value && i !== editingEndpointIndex
    );

    if (isExists) throw new Error("Enpoint name is already exists");
  };

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidMount(): void {
    this.handleLoadEndpoint();
  }

  componentDidUpdate(
    prevProps: Readonly<IPureDesignComponentProps & IWithSDProps & ICreateRestApiEndpointProps>
  ): void {
    const { editingDatasource, editingEndpointIndex } = this.props;
    const { editingDatasource: prevEditingDatasource, editingEndpointIndex: prevEditingEndpointIndex } = prevProps;
    if (prevEditingDatasource.id !== editingDatasource.id || prevEditingEndpointIndex !== editingEndpointIndex) {
      this.handleLoadEndpoint();
    }
    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleCloseDatasource();
    }
  }

  handleLoadEndpoint = () => {
    // Extract only the specific values we need without cloning
    const editingDatasource = this.props.editingDatasource;
    const editingEndpointIndex = this.props.editingEndpointIndex;
    const paths = editingDatasource.restApiConnectionRequest.paths;
    const schemas = editingDatasource.restApiConnectionRequest.schemas || [];

    let endpointValue: IRestEndpointRequest = { path: "", method: "GET" };
    const endpointName = `MyEndpoint${(paths?.length || 0) + 1}`;
    let userNameHeaderParameterIndex = -1;
    let authTokenHeaderParameterIndex = -1;
    let basicAuthHeaderParameterIndex = -1;
    let basicAuthFormValues = { username: "", password: "" };

    if (paths && editingEndpointIndex >= 0) {
      const endpoint = paths[editingEndpointIndex];
      endpointValue.method = endpoint.operations[0].type;
      endpointValue.path = endpoint.path.substring(1);
      const parsedHeaders: IParsedHeaderParameters = parseHeaderRecord(
        endpoint.operations[0].request.extraHeaderParameters,
        endpoint.operations[0].request.authType
      );
      const organizedHeaders: IOrganizedHeaderParameters = organizeHeaderParameters(parsedHeaders.parameters);
      // Both tables end on the blank row the next entry is typed into.
      const extraHeaderParameters: IApiKeyParameter[] = ensureTrailingDraftRow(
        organizedHeaders.parameters,
        isBlankHeaderParameter,
        blankHeaderParameter
      );
      const parameters: IRestEndpointParameterRow[] = ensureTrailingDraftRow(
        endpoint.operations[0].parameters,
        isBlankEndpointParameter,
        blankEndpointParameter
      );
      basicAuthFormValues = parsedHeaders.basicAuth;
      userNameHeaderParameterIndex = organizedHeaders.userNameHeaderParameterIndex;
      authTokenHeaderParameterIndex = organizedHeaders.authTokenHeaderParameterIndex;
      basicAuthHeaderParameterIndex = organizedHeaders.basicAuthHeaderParameterIndex;

      this.setState(
        {
          endpointName: endpoint.operations[0].operationName,
          isRequestBodyEditable: this.getRequestBodyVisibilityStatus(endpointValue.method),
          isNameOnEdit: false,
          authType: endpoint.operations[0].request.authType || EAuthenticationType.InheritFromCollection,
          request: endpoint.operations[0].request,
          responses: endpoint.operations[0].responses,
          isResponseBodyEdited: endpoint.operations[0].responses[0].isBodyEdited,
          schemas,
          userNameHeaderParameterIndex,
          authTokenHeaderParameterIndex,
          basicAuthHeaderParameterIndex,
          showPlaceholderOnResponseBody: false
        },
        () => {
          this.formRef.current?.setFieldsValue({
            name: endpoint.operations[0].operationName,
            httpRequest: endpointValue,
            parameters,
            BasicAuthUsername: basicAuthFormValues.username,
            BasicAuthPassword: basicAuthFormValues.password,
            ClientIdKeyName: endpoint.operations[0].request.clientIdKeyName,
            ClientId: endpoint.operations[0].request.clientId,
            ClientSecretKeyName: endpoint.operations[0].request.clientSecretKeyName,
            ClientSecret: endpoint.operations[0].request.clientSecret,
            CurrentUserEmailName: endpoint.operations[0].request.currentUserEmailName,
            requestBody: endpoint.operations[0].request.body || "",
            responseBody: endpoint.operations[0].responses[0].body || "",
            extraHeaderParameters
          });

          this.objectHash = this.getObjectHash();
          this.executeCheckerHash = this.getExecuteCheckerHash();
        }
      );
      return;
    }
    this.setState(
      {
        endpointName,
        isRequestBodyEditable: false,
        authType: EAuthenticationType.InheritFromCollection,
        request: { schema: null, type: "object" },
        responses: [{ schema: null, type: "object", errorMessage: "" }],
        isResponseBodyEdited: false,
        schemas,
        userNameHeaderParameterIndex,
        authTokenHeaderParameterIndex,
        basicAuthHeaderParameterIndex,
        showPlaceholderOnResponseBody: true
      },
      () => {
        this.formRef.current?.setFieldsValue({
          name: endpointName,
          httpRequest: endpointValue,
          parameters: [blankEndpointParameter()],
          BasicAuthUsername: "",
          BasicAuthPassword: "",
          ClientIdKeyName: "",
          ClientId: "",
          ClientSecretKeyName: "",
          ClientSecret: "",
          CurrentUserEmailName: "",
          requestBody: "",
          responseBody: "",
          extraHeaderParameters: [blankHeaderParameter()]
        });
        this.objectHash = this.getObjectHash();
        this.executeCheckerHash = "";
      }
    );
  };

  getRequestBodyVisibilityStatus = (method?: HTTPMethod) => {
    return method !== "GET" && method !== "DELETE";
  };

  obtainRestApiConnectionRequest = (formValues, testRun: boolean = false) => {
    // Extract only the specific values we need without cloning
    const editingDatasource = this.props.editingDatasource;
    const editingEndpointIndex = this.props.editingEndpointIndex;
    const { authType, endpointName, request, responses, schemas } = this.state;

    const restApiConnectionRequest: IRestServiceConnectionDto = {
      ...editingDatasource.restApiConnectionRequest
    };

    if (!restApiConnectionRequest.paths) restApiConnectionRequest.paths = [];
    if (!restApiConnectionRequest.schemas) restApiConnectionRequest.schemas = [];

    const credentials: IBasicAuthCredentials = {
      username: formValues.BasicAuthUsername,
      password: formValues.BasicAuthPassword
    };
    const headers: Record<string, string> = buildHeaderRecord(formValues.extraHeaderParameters, credentials);
    const endpoint: IRestApiEndpoint = {
      operations: [
        {
          operationName: endpointName,
          type: formValues.httpRequest.method,
          // The grids' draft rows are where the next entry goes, not entries (the headers' are
          // dropped by `buildHeaderRecord`). The DTO types the list as an empty tuple.
          parameters: withoutBlankEndpointParameters(formValues.parameters) as [],
          request: {
            ...request,
            authType,
            extraHeaderParameters: headers,
            clientIdKeyName: formValues.ClientIdKeyName,
            clientId: formValues.ClientId,
            clientSecret: formValues.ClientSecret,
            clientSecretKeyName: formValues.ClientSecretKeyName,
            currentUserEmailName: formValues.CurrentUserEmailName,
            body: formValues.requestBody
          },
          responses: responses.map((res) => ({
            ...res,
            body: formValues.responseBody,
            isBodyEdited: this.state.isResponseBodyEdited
          }))
        }
      ],
      path: `/${formValues.httpRequest.path}`
    };

    if (restApiConnectionRequest.paths.length && editingEndpointIndex >= 0) {
      let editingEndpoint = restApiConnectionRequest.paths[editingEndpointIndex];
      const operation = { ...editingEndpoint.operations[0] };

      if (testRun) endpoint.operations[0].request.schema = "";
      endpoint.operations[0].responses = operation.responses.map((res, i) => ({
        ...res,
        body: formValues.responseBody,
        isBodyEdited: this.state.isResponseBodyEdited,
        type: endpoint.operations[0].responses[i].type
      }));

      restApiConnectionRequest.paths[editingEndpointIndex] = endpoint;
    } else {
      restApiConnectionRequest.paths = [...restApiConnectionRequest.paths, endpoint];
    }

    restApiConnectionRequest.schemas = [...schemas];

    return { ...restApiConnectionRequest };
  };

  handleExecutePath = () => {
    if (this.state.isNameOnEdit) {
      KMainFunctions.displayWarningNotification("You should apply your API Endpoint Name before Run the Endpoint");

      return;
    }
    this.setState({ isSaveDisabled: true });
    this.formRef.current
      ?.validateFields()
      .then(async (formValues) => {
        const restApiConnectionRequest = this.obtainRestApiConnectionRequest(formValues, true);
        await DatasourceService.ExecuteApiEndpoint({ restApiConnectionRequest, operationName: this.state.endpointName })
          .then((response) => {
            if (response.status === 200) {
              const { editingEndpointIndex } = { ...this.props };
              const { request: pathRequest, responses: pathResponses } =
                response.data.paths[editingEndpointIndex > -1 ? editingEndpointIndex : response.data.paths.length - 1]
                  .operations[0];
              const schemas = response.data.schemas;

              const pathResponse = pathResponses[0];
              if (pathResponse.errorMessage) {
                KMainFunctions.displayWarningNotification(pathResponse.errorMessage, {
                  title: `Error: Execution of "${this.state.endpointName}" returned error"`
                });
                this.formRef.current?.setFieldsValue({ ...formValues, responseBody: "" });
                return;
              }

              this.setState(
                {
                  request: pathRequest,
                  responses: pathResponses,
                  schemas,
                  showPlaceholderOnResponseBody: false
                },
                () => {
                  this.formRef.current?.setFieldsValue({ ...formValues, responseBody: pathResponse.body });
                  this.executeCheckerHash = this.getExecuteCheckerHash();
                }
              );

              KMainFunctions.displaySuccessNotification(
                `Method: ${formValues.httpRequest.method} Path: /${formValues.httpRequest.path} Name: ${this.state.endpointName} is executed successfully`
              );
            }
          })
          .catch((_err) => {
            this.formRef.current?.setFieldsValue({ ...formValues, responseBody: "" });
            KMainFunctions.displayWarningNotification("Error while executing Rest API Endpoint connection");
          })
          .finally(() => {
            this.setState({ isSaveDisabled: false });
          });
      })
      .catch((error) => {
        if (error.errorFields) this.handleFormError(error);
      })
      .finally(() => {
        this.setState({ isSaveDisabled: false });
      });
  };

  saveEndpoint = async (formValues: any) => {
    if (this.state.isNameOnEdit) {
      KMainFunctions.displayWarningNotification("You should apply your API Endpoint Name before save");

      return;
    }
    if (this.handleCheckExcetuteObligation()) {
      KMainFunctions.displayWarningNotification("You should Run your API Endpoint before save");
      return;
    }
    // The commit button is live again as soon as the ✓ replaces the spinner, so a second press
    // would re-fire the save behind the closing sheet.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();

    // Create a shallow copy of the editingDatasource to avoid mutation
    let editingDatasource = { ...this.props.editingDatasource };
    if (!editingDatasource.id) {
      const savedEditingDatasource = this.props.sd.datasources.find(
        (ds) => ds.name === editingDatasource.name && ds.datasourceType === editingDatasource.datasourceType
      );

      if (!savedEditingDatasource) {
        KMainFunctions.displayWarningNotification("Datasource is not found please try again later");
        this.saveStatus.reset();
        return;
      }

      // Create a shallow copy of the found datasource
      editingDatasource = { ...savedEditingDatasource };
    }
    const restApiConnectionRequest = this.obtainRestApiConnectionRequest(formValues, false);
    editingDatasource.restApiConnectionRequest = restApiConnectionRequest;

    await DatasourceService.SaveDatasource(editingDatasource)
      .then(async (response): Promise<void> => {
        if (response.status !== 200) {
          // A resolved response that is not 200: the `finally` reset used to rescue this branch.
          this.saveStatus.fail();
          return;
        }
        this.props.sd.refreshActions();
        await this.props.sd.refreshDatabases();
        // The ✓ goes up first and the sheet leaves after it — see `SaveStatus`.
        // A close confirmation this save answered ("Update & close") steps aside at once, so
        // the ✓ on the button is what is seen before the editor leaves.
        this.props.setShowModal(false);
        this.saveStatus.succeed((): void => {
          // The list behind the sheet lights the row up; the button has already said "Saved".
          requestDatasourceReveal({ name: editingDatasource.name ?? "" });
          this.closeDataSource();
        });
      })
      .catch((_err): void => {
        this.saveStatus.fail();
        KMainFunctions.displayWarningNotification("Error while saving Rest API connection");
      });
  };

  getExecuteCheckerHash(): string {
    const { authType, endpointName } = this.state;
    const {
      httpRequest,
      parameters,
      ClientIdKeyName,
      ClientId,
      ClientSecretKeyName,
      ClientSecret,
      CurrentUserEmailName,
      requestBody,
      responseBody,
      extraHeaderParameters
    } = this.formRef.current?.getFieldsValue();

    // Without the grids' blank rows: a draft row appearing, or one typed into and cleared again, is
    // not a change to the request, and must not demand another Run before the endpoint can be saved.
    return ObjectHash({
      authType,
      extraHeaderParameters: withoutBlankRows(extraHeaderParameters, isBlankHeaderParameter),
      endpointName,
      httpRequest,
      parameters: withoutBlankEndpointParameters(parameters),
      ClientIdKeyName,
      ClientId,
      ClientSecretKeyName,
      ClientSecret,
      CurrentUserEmailName,
      requestBody,
      responseBody
    });
  }

  handleCheckExcetuteObligation = () => {
    return this.executeCheckerHash !== this.getExecuteCheckerHash();
  };

  getObjectHash(): string {
    const { authType, endpointName } = this.state;
    const {
      httpRequest,
      parameters,
      BasicAuthUsername,
      BasicAuthPassword,
      ClientIdKeyName,
      ClientId,
      ClientSecretKeyName,
      ClientSecret,
      CurrentUserEmailName,
      requestBody,
      responseBody,
      extraHeaderParameters
    } = this.formRef.current?.getFieldsValue();

    // `endpointName` is the editor's own name; the rename FIELD is not compared, because it
    // only exists while a rename is open and `getFieldsValue()` reports registered fields
    // only — an untouched endpoint would otherwise look edited. The grids' blank rows are left
    // out for the same reason: the draft rows arrive after the baseline is taken.
    return ObjectHash({
      endpointName,
      authType,
      extraHeaderParameters: withoutBlankRows(extraHeaderParameters, isBlankHeaderParameter),
      httpRequest,
      parameters: withoutBlankEndpointParameters(parameters),
      BasicAuthUsername,
      BasicAuthPassword,
      ClientIdKeyName,
      ClientId,
      ClientSecretKeyName,
      ClientSecret,
      CurrentUserEmailName,
      requestBody,
      responseBody
    });
  }

  handleCloseDatasource() {
    // No baseline yet: handleLoadEndpoint takes it in a setState callback, which React runs AFTER
    // componentDidUpdate, so a navigation already pending when the editor mounts is checked before
    // the form is even seeded. Nothing can have been edited yet; let it through.
    if (!this.objectHash || this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  }

  handleSubmitNameChange = () => {
    const errors = this.formRef.current?.getFieldError("name");
    if (errors && errors.length > 0) {
      return;
    }
    this.setState({ isNameOnEdit: false, endpointName: this.formRef.current?.getFieldValue("name") });
  };

  handleAbortNameChange = () => {
    if (!this.state.endpointName) {
      KMainFunctions.displayWarningNotification("Please enter a valid endpoint name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.endpointName
    });
    this.setState({ isNameOnEdit: false });
  };

  handleNameInputOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    this.handleSubmitNameChange();
  };

  handleSetExtraHeaderParameters = (extraHeaderParameters: IApiKeyParameter[]) => {
    const formValues = { ...this.formRef.current?.getFieldsValue() };
    formValues.extraHeaderParameters = extraHeaderParameters;
    this.formRef.current?.setFieldsValue({ ...formValues });
  };

  handleCheckAndOrganizeExtraHeaderParameters = (extraHeaderParameters: IApiKeyParameter[]) => {
    const organized: IOrganizedHeaderParameters = organizeHeaderParameters(extraHeaderParameters);

    this.setState(
      {
        userNameHeaderParameterIndex: organized.userNameHeaderParameterIndex,
        authTokenHeaderParameterIndex: organized.authTokenHeaderParameterIndex,
        basicAuthHeaderParameterIndex: organized.basicAuthHeaderParameterIndex
      },
      () => {
        this.handleSetExtraHeaderParameters(organized.parameters);
      }
    );
  };

  handleSelectAuthType = (authType: EAuthenticationType) => {
    this.setState(
      {
        authType,
        userNameHeaderParameterIndex: -1,
        authTokenHeaderParameterIndex: -1,
        basicAuthHeaderParameterIndex: -1
      },
      () => {
        const rows: IApiKeyParameter[] = headerParametersForAuthType(
          authType,
          this.formRef.current?.getFieldValue("extraHeaderParameters")
        );
        this.handleSetExtraHeaderParameters(rows);
        this.handleCheckAndOrganizeExtraHeaderParameters(rows);
      }
    );
  };

  /**
   * Run validates the whole form first. The panes used to be tabs, so a rejected field was
   * reported by switching to its tab and nothing else; stacked, the message itself is what
   * the reader needs.
   */
  handleFormError = (error: IEndpointValidationError): void => {
    const message: string | undefined = error.errorFields?.find(
      (field: IEndpointValidationErrorField): boolean => field.errors.length > 0
    )?.errors[0];
    KMainFunctions.displayWarningNotification(message ?? "Please correct the endpoint before running it");
  };

  /**
   * The endpoints of the collection this one belongs to. A path has no id of its own, so it
   * is addressed by its index — the same way the left tree opens one — and the list is read
   * from the LIVE datasource rather than the editor's snapshot, so a rename or a delete
   * elsewhere is reflected.
   */
  private switcherItems = (): KuikaEntitySwitcherItem[] => {
    const live: IDatasourceDto | undefined = (this.props.sd.datasources ?? []).find(
      (datasource: IDatasourceDto): boolean => datasource.id === this.props.editingDatasource.id
    );
    const paths: IRestApiEndpoint[] = live?.restApiConnectionRequest?.paths ?? [];
    return paths.map((path: IRestApiEndpoint, index: number): KuikaEntitySwitcherItem => ({
      id: String(index),
      name: path.operations?.[0]?.operationName || `Endpoint ${index + 1}`
    }));
  };

  /** The endpoint's chip: its glyph carries the collection — what the old subtitle said. */
  private renderSwitcher = (): React.ReactNode => {
    const glyph: IEditorGlyph = getEditorGlyph({ kind: "endpoint", collectionName: this.props.editingDatasource.name });
    return (
      <KuikaEntitySwitcher
        trigger="chip"
        triggerIcon={<EditorGlyphIcon glyph={glyph} />}
        triggerIconTooltip={glyph.label}
        triggerLabel={this.state.endpointName}
        triggerTestId="restApiEndpointSwitcherTrigger"
        onManageAll={() => this.handleCloseDatasource()}
        items={this.switcherItems()}
        current={{ id: String(this.props.editingEndpointIndex), name: this.state.endpointName }}
        entityLabel="Endpoint"
        iconName="cloud-cog"
        iconType="datasource-icons"
        onSelect={(item: KuikaEntitySwitcherItem): void => {
          // Parked as the pending close, so the editor's own guard gets to offer a save
          // before the endpoint under it changes.
          this.props.setNextCallbackFunction((): void =>
            this.props.sd.setActiveEditingDatasource(this.props.editingDatasource, Number(item.id))
          );
        }}
        onCreateNew={(): void => {
          // -1 is what the tree passes for "a new endpoint on this collection".
          this.props.setNextCallbackFunction((): void =>
            this.props.sd.setActiveEditingDatasource(this.props.editingDatasource, -1)
          );
        }}
      />
    );
  };

  private renderName = (title: string): React.ReactNode => (
    <ConnectionRenameTitle
      title={title}
      identity={this.renderSwitcher()}
      isEditing={this.state.isNameOnEdit}
      canDiscard={Boolean(this.state.endpointName)}
      onBeginEdit={() => this.setState({ isNameOnEdit: true })}
      onSubmit={this.handleSubmitNameChange}
      onDiscard={this.handleAbortNameChange}
      placeholder="Endpoint Name"
      rules={[
        { required: true, message: "Please enter a Endpoint name" },
        { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Endpoint Name is invalid" },
        { validator: this.existingEndpointNameValidator }
      ]}
      renameTestId="restApiEndpointNameEditButton"
      inputTestId="restApiEndpointNameInput"
      submitTestId="restApiEndpointNameSubmitButton"
    />
  );

  /**
   * The collection this endpoint runs under — what the bar's subtitle used to say. The base
   * URL is the half of the request the Request row does not show.
   */
  private renderCollection(): ReactNode {
    const { editingDatasource } = this.props;
    const baseUrl: string | undefined = editingDatasource.restApiConnectionRequest?.serviceBaseUrl;

    return (
      <KuikaFormAsideBlock title="Collection" testId="restApiEndpointCollectionBlock">
        <span className="kuika-connection-form__usage">{editingDatasource.name}</span>
        {baseUrl && <code className="kuika-connection-form__resolved">{baseUrl}</code>}
      </KuikaFormAsideBlock>
    );
  }

  render(): ReactNode {
    const { editingEndpointIndex } = this.props;
    const authOptions: KuikaTabOption[] = buildAuthTypeTabOptions(ENDPOINT_AUTH_TYPES, "restApiEndpoint");

    return (
      <>
        <KuikaFormPage
          className="kuika-connection-form"
          title={this.state.endpointName}
          formName="dsModal"
          formRef={this.formRef}
          onFinish={this.saveEndpoint}
          status={this.state.saveStatus}
          isLoading={this.state.isSaveDisabled}
          renderTitle={this.renderName}
          submitLabel={editingEndpointIndex > -1 ? "UPDATE" : "CREATE"}
          submitTestId="restApiEndpointSubmitButton"
          cancelTestId="restApiEndpointCancelButton"
          onCancel={() => this.handleCloseDatasource()}
          aside={this.renderCollection()}
          learnMore={{
            label: `${REST_WO_SWAGGER_SPEC.copyName} user guide`,
            href: REST_API_GUIDE,
            testId: "restApiEndpointLearnMoreLink"
          }}
        >
          {/* Sections rather than tabs. The three panes were a response to a cramped panel;
              on a page they can all be on screen, which is also what let the tab-jumping on
              a validation error and after a run go away. */}
          <KuikaFormSection legend="Request" hint="Under the collection's base URL" testId="restApiEndpointSection">
            <KuikaFormField
              name="httpRequest"
              className="kuika-form-section__wide"
              rules={[{ validator: this.validateHttpRequest }]}
              extra="Run the endpoint before saving it — the response defines its body."
            >
              <RestEndpointRequestRow
                onRun={this.handleExecutePath}
                isRunning={this.state.isSaveDisabled}
                onChange={(value: IRestEndpointRequest) => {
                  this.setState({ isRequestBodyEditable: this.getRequestBodyVisibilityStatus(value.method) });
                }}
              />
            </KuikaFormField>
          </KuikaFormSection>

          <KuikaFormSection
            legend="Parameters"
            hint="Sent with every call to this endpoint"
            testId="restApiEndpointParametersSection"
          >
            <RestEndpointParametersTable />
          </KuikaFormSection>

          <KuikaFormSection
            legend="Authentication"
            hint="How Kuika identifies itself"
            columns={2}
            testId="restApiEndpointCredentialsSection"
          >
            {/* Not a form field: the auth type decides which rows the header table carries,
                so the form reacts to it as state and persists it from there. The collection
                form's segmented control plus "Inherit from collection" first — see there. */}
            <div className="kuika-form-field kuika-form-section__wide">
              <span className="kuika-form-field__label">
                Authentication type
                <InputTooltip
                  description="You can choose the authentication method to use to access the API."
                  helpLink={REST_API_GUIDE}
                />
              </span>
              <KuikaTabs
                size="lg"
                ariaLabel="Authentication type"
                dataTestId="restApiEndpointAuthTypeSelect"
                options={authOptions}
                value={String(this.state.authType)}
                onChange={(value: string): void => this.handleSelectAuthType(Number(value) as EAuthenticationType)}
              />
            </div>
            <RestConnectionAuthSection
              authType={this.state.authType}
              userNameHeaderParameterIndex={this.state.userNameHeaderParameterIndex}
              authTokenHeaderParameterIndex={this.state.authTokenHeaderParameterIndex}
              basicAuthHeaderParameterIndex={this.state.basicAuthHeaderParameterIndex}
              helpLink={REST_API_GUIDE}
              testIdPrefix="restApiEndpoint"
            />
          </KuikaFormSection>

          <KuikaFormSection legend="Body" hint="What goes out, and what came back" testId="restApiEndpointBodySection">
            <RestApiEndpointBodyTab
              isRequestBodyEditable={this.state.isRequestBodyEditable}
              isResponseBodyEditable={this.state.isResponseBodyEdited}
              showPlaceholderOnResponseBody={this.state.showPlaceholderOnResponseBody}
              handleChangeResponseEditorStatus={(isResponseBodyEdited: boolean) => {
                this.setState({ isResponseBodyEdited, showPlaceholderOnResponseBody: false });
              }}
            />
          </KuikaFormSection>
        </KuikaFormPage>
        <CloseConfirmationModal
          itemName={this.state.endpointName}
          closeType={"Rest API Endpoint"}
          triggerModal={this.props.showModal}
          isLoading={this.state.saveStatus === "loading"}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch: (arg0: UnsavedChangesModalModuleActionTypes) => any) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createApiEndpoint = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ICreateRestApiEndpointProps>(CreateRestApiEndpoint));
export { createApiEndpoint as CreateRestApiEndpoint };
